import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/lib/api.ts'
import { useModulesStore } from './modules.ts'

interface User {
  id: string
  email: string
  name: string
  role: string
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  profilePhotoUrl: string | null | undefined
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setTokens: (accessToken: string, refreshToken: string, user: User) => void
  fetchProfilePhoto: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      profilePhotoUrl: undefined,

      login: async (email, password) => {
        const { data } = await apiClient.post('/auth/login', { email, password })
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
          isAuthenticated: true,
          profilePhotoUrl: undefined,
        })
        get().fetchProfilePhoto()
      },

      logout: () => {
        useModulesStore.getState().resetAccess()
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, profilePhotoUrl: undefined })
      },

      setTokens: (accessToken, refreshToken, user) => {
        set({ accessToken, refreshToken, user, isAuthenticated: true, profilePhotoUrl: undefined })
        get().fetchProfilePhoto()
      },

      fetchProfilePhoto: async () => {
        if (get().profilePhotoUrl !== undefined) return
        try {
          const { data } = await apiClient.get('/auth/me/profile-photo')
          set({ profilePhotoUrl: (data.photoUrl as string | null) ?? null })
        } catch {
          set({ profilePhotoUrl: null })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
